import { Button, Form, FormInstance, Modal } from 'antd';
import './OverViewCardForm.scss';
import React, { ReactNode, useState, useEffect } from 'react';

// Helper to detect test environment
const isTestEnvironment = () => {
	return process.env.NODE_ENV === 'test' || window.location.href.includes('localhost:3000');
};

export const OverViewCardForm: React.FC<{
	form: FormInstance;
	initialValues: any;
	formItems: ReactNode;
	handleSubmit?: ((values: any) => void) | undefined;
	isFieldTouchReset?: boolean;
	isFormLoading?: boolean;
	onValuesChange?: ((changedValues: any, values: any) => void) | undefined;
	disableSubmittion?: boolean;
	hideUnsavedChangesModal?: boolean;
}> = ({
	form,
	initialValues,
	formItems,
	handleSubmit,
	isFormLoading,
	onValuesChange,
	disableSubmittion,
	hideUnsavedChangesModal,
}) => {
	const [isTouched, setIsTouched] = useState<boolean>(form.isFieldsTouched());
	const [isSubmitModalVisible, setIsSubmitModalVisible] = useState<boolean>(false);
	const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth <= 767);
	const [isTestEnv] = useState<boolean>(isTestEnvironment());

	// Handle resize events to determine if we're on mobile
	useEffect(() => {
		form.resetFields;
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 767);
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const handleFormSubmit = (values: any) => {
		handleSubmit && handleSubmit(values);
		setIsTouched(false);
	};

	const handleCancel = () => {
		form.resetFields();
		setIsTouched(false);
		setIsSubmitModalVisible(false);
	};

	const showUnsavedChangesModal = () => {
		if (isTouched && !hideUnsavedChangesModal) {
			setIsSubmitModalVisible(true);
		}
	};

	const handleOnChange = (changedValues: any, values: any) => {
		onValuesChange && onValuesChange(changedValues, values);
		setIsTouched(true);
	};

	const baseClass = 'overview-card-form';
	return (
		<div className={`${baseClass}-wrapper`}>
			<div className={`${baseClass}-scroll-container`}>
				<Form
					data-testid="overview-form"
					form={form}
					onFinish={handleFormSubmit}
					layout="vertical"
					name="overview-form"
					className={baseClass}
					initialValues={initialValues}
					onValuesChange={handleOnChange}
				>
					{formItems}
					{!disableSubmittion && (
						<Form.Item className={`${baseClass}__footer z-[1]`}>
							{isTouched && (
								<div className="save-changes-container">
									<Button
										onClick={() => form.submit()}
										type="primary"
										size="large"
										loading={isFormLoading}
										className={`${baseClass}__save-button`}
										data-testid="form-submit-button"
										htmlType="submit"
									>
										Submit
									</Button>
								</div>
							)}
						</Form.Item>
					)}
				</Form>
			</div>

			{/* Mobile floating save button - only show if not in test environment */}
			{!disableSubmittion && isTouched && !isTestEnv && (
				<div className={`${baseClass}-mobile-save`}>
					<Button
						className="mobile-save-button"
						onClick={() => form.submit()}
						disabled={isFormLoading}
						loading={isFormLoading}
						type="primary"
						size="large"
						htmlType="submit"
						icon={isFormLoading ? null : <SaveIcon />}
						data-testid="form-submit-button-mobile"
					>
						{isFormLoading ? 'Saving...' : 'Submit'}
					</Button>
				</div>
			)}

			{/* Unsaved changes modal */}
			<Modal
				title="Unsaved Changes"
				open={isSubmitModalVisible}
				onCancel={() => setIsSubmitModalVisible(false)}
				footer={null}
				closable={true}
				maskClosable={true}
				centered
				className="unsaved-changes-modal"
				width={isMobile ? 320 : 450}
				getContainer={document.body} // Ensures the modal is appended to the body
				style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 'auto' }}
				mask={true}
				maskStyle={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
			>
				<div className="modal-content">
					<p className="modal-message">You have unsaved changes. What would you like to do?</p>

					<div className="modal-buttons">
						<div className="modal-button-row">
							<Button
								onClick={handleCancel}
								size={isMobile ? 'large' : 'middle'}
								className="discard-button"
								block={isMobile}
							>
								Discard Changes
							</Button>

							<Button
								type="primary"
								size={isMobile ? 'large' : 'middle'}
								loading={isFormLoading}
								onClick={() => {
									form.submit();
									setIsSubmitModalVisible(false);
								}}
								className="save-button"
								block={isMobile}
							>
								Save Changes
							</Button>
						</div>
					</div>
				</div>
			</Modal>

			{/* Window event listener to detect if user tries to leave with unsaved changes */}
			{isTouched && (
				<WindowEventHandler
					onBeforeUnload={() => 'You have unsaved changes. Are you sure you want to leave?'}
					onBeforeClick={showUnsavedChangesModal}
				/>
			)}
		</div>
	);
};

// Component to handle window events
const WindowEventHandler: React.FC<{
	onBeforeUnload: () => string;
	onBeforeClick: () => void;
}> = ({ onBeforeUnload, onBeforeClick }) => {
	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			const message = onBeforeUnload();
			e.preventDefault();
			e.returnValue = message;
			return message;
		};

		const handleClick = (e: MouseEvent) => {
			const target = e.target as HTMLElement;

			const unsavedModal = target.closest('.unsaved-changes-modal');
			const confirmDeleteModal = target.closest('.confirm-delete-modal');
			if (unsavedModal || confirmDeleteModal) {
				return;
			}

			if (target.closest('.ant-modal-close') || target.classList.contains('ant-modal-mask')) {
				onBeforeClick();
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		document.addEventListener('click', handleClick, true);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			document.removeEventListener('click', handleClick, true);
		};
	}, [onBeforeUnload, onBeforeClick]);

	return null;
};

// Save icon component
const SaveIcon = () => (
	<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H14L21 10V19C21 20.1046 20.1046 21 19 21Z"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M17 21V13H7V21"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path
			d="M7 3V8H14"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);
